import { SchemaInput } from '../types/common';
import type { ContactPointInput, BreadcrumbItem, EnhancedWebSiteInput } from '../types/company';
import { BaseVideoItem } from '../types/video';
import { Result, ok, fail } from './result';

/**
 * Interface representing a validation error with field context.
 */
export interface ValidationError {
    field: string;
    message: string;
    value?: unknown;
}

/**
 * Safely converts a value to a number with fallback.
 *
 * @param value - Value to convert to number
 * @param fallback - Fallback value if conversion fails (default: 0)
 * @returns Number value or fallback
 */
export function safeNumber(value: unknown, fallback = 0): number {
    if (typeof value === 'number' && !isNaN(value)) {
        return value;
    }

    const num = Number(value);
    return isNaN(num) ? fallback : num;
}

/**
 * Safely converts a value to a string with fallback.
 *
 * @param value - Value to convert to string
 * @param fallback - Fallback value if conversion fails (default: empty string)
 * @returns String value or fallback
 */
export function safeString(value: unknown, fallback = ''): string {
    if (typeof value === 'string') return value;
    if (value == null) return fallback;
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return String(value);
}

/**
 * Validates if a string is a valid URL.
 *
 * @param url - URL string to validate
 * @returns True if URL is valid, false otherwise
 */
export function isValidUrl(url: string): boolean {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
}

/**
 * Decodes common HTML entities to their character equivalents.
 *
 * @param htmlString - HTML string containing entities
 * @returns Decoded string with entities converted to characters
 */
export function decodeHtmlEntities(htmlString: string): string {
    if (!htmlString) return '';

    return htmlString
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ');
}

/**
 * Strips HTML tags from a string and decodes HTML entities.
 * Removes script and style tags completely for security.
 *
 * @param html - HTML string to clean
 * @returns Plain text with HTML tags removed and entities decoded
 */
export function stripHtmlTags(html: string): string {
    if (!html || typeof html !== 'string') return '';

    const clean = html
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<[^>]*>/g, '')
        .trim();

    return decodeHtmlEntities(clean);
}

/**
 * Converts a relative URL to an absolute URL using the provided base URL.
 *
 * @param url - URL to convert (can be relative or absolute)
 * @param baseUrl - Base URL to use for relative URLs (default: Storyblocks domain)
 * @returns Absolute URL string
 */
export function toAbsoluteUrl(url: string, baseUrl = 'https://www.storyblocks.com'): string {
    if (!url) return '';

    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }

    const cleanBase = baseUrl.replace(/\/$/, '');
    const cleanPath = url.startsWith('/') ? url : `/${url}`;

    return `${cleanBase}${cleanPath}`;
}

/**
 * Validates a video item for schema generation.
 *
 * @param item - Unknown item to validate as video data
 * @returns Result containing validated BaseVideoItem or validation errors
 */
function validateVideoItem(item: unknown): Result<BaseVideoItem, ValidationError[]> {
    const errors: ValidationError[] = [];

    if (!item || typeof item !== 'object') {
        return fail([{ field: 'item', message: 'Item must be an object' }]);
    }

    const videoItem = item as Record<string, unknown>;

    if (!videoItem.id) {
        errors.push({ field: 'id', message: 'Video ID is required' });
    }

    if (!videoItem.title) {
        errors.push({ field: 'title', message: 'Video title is required' });
    }

    const urlFields = ['thumbnailUrl', 'previewUrl', 'detailsUrl', 'mediumImageUrl', 'largeImageUrl'];
    for (const field of urlFields) {
        const value = videoItem[field];
        if (value && typeof value === 'string' && !isValidUrl(toAbsoluteUrl(value))) {
            errors.push({
                field,
                message: `Invalid URL format`,
                value,
            });
        }
    }

    const numericFields = [
        'duration',
        'maxResolution',
        'aspectRatio',
        'fileSize',
        'bitrate',
        'rating',
        'ratingCount',
        'viewCount',
        'downloadCount',
    ];
    for (const field of numericFields) {
        const value = videoItem[field];
        if (value !== undefined && typeof value !== 'number') {
            const converted = Number(value);
            if (isNaN(converted)) {
                errors.push({
                    field,
                    message: `Must be a valid number`,
                    value,
                });
            }
        }
    }

    if (videoItem.rating !== undefined) {
        const rating = safeNumber(videoItem.rating);
        if (rating < 0 || rating > 5) {
            errors.push({
                field: 'rating',
                message: 'Rating must be between 0 and 5',
                value: rating,
            });
        }
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok(videoItem as unknown as BaseVideoItem);
}

/**
 * Validates schema input data for general schema generation.
 *
 * @param item - Unknown item to validate as schema input
 * @returns Result containing validated SchemaInput or validation errors
 */
function validateSchemaInput(item: unknown): Result<SchemaInput, ValidationError[]> {
    const errors: ValidationError[] = [];

    if (!item || typeof item !== 'object') {
        return fail([{ field: 'item', message: 'Item must be an object' }]);
    }

    const schemaItem = item as Record<string, unknown>;

    if (!schemaItem.title) {
        errors.push({ field: 'title', message: 'Title is required' });
    }

    const urlFields = ['image', 'url'];
    for (const field of urlFields) {
        const value = schemaItem[field];
        if (value && typeof value === 'string' && !isValidUrl(toAbsoluteUrl(value))) {
            errors.push({
                field,
                message: `Invalid URL format`,
                value,
            });
        }
    }

    if (schemaItem.price !== undefined) {
        const price = Number(schemaItem.price);
        if (isNaN(price) || price < 0) {
            errors.push({
                field: 'price',
                message: 'Price must be a positive number',
                value: schemaItem.price,
            });
        }
    }

    if (schemaItem.currency && typeof schemaItem.currency === 'string') {
        if (schemaItem.currency.length !== 3) {
            errors.push({
                field: 'currency',
                message: 'Currency must be a 3-letter code (e.g., USD)',
                value: schemaItem.currency,
            });
        }
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok(schemaItem as unknown as SchemaInput);
}

/**
 * Validates an array of items using a provided validator function.
 *
 * @template T - Type of validated items
 * @param items - Array of unknown items to validate
 * @param validator - Function to validate individual items
 * @returns Result containing array of validated items or aggregated errors
 */
export function validateBatch<T>(
    items: unknown[],
    validator: (item: unknown) => Result<T, ValidationError[]>,
): Result<T[], ValidationError[]> {
    const validated: T[] = [];
    const allErrors: ValidationError[] = [];

    items.forEach((item, index) => {
        const result = validator(item);
        if (result.success) {
            validated.push(result.data);
        } else {
            result.error.forEach((err) => {
                allErrors.push({
                    ...err,
                    field: `[${index}].${err.field}`,
                });
            });
        }
    });

    if (allErrors.length > 0) {
        return fail(allErrors);
    }

    return ok(validated);
}

/**
 * Validates if a string is a valid email address format.
 *
 * @param email - Email string to validate
 * @returns True if email format is valid, false otherwise
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Validates if a string is a valid phone number format.
 * Accepts international format with optional + prefix.
 *
 * @param phone - Phone number string to validate
 * @returns True if phone format is valid, false otherwise
 */
export function isValidPhoneNumber(phone: string): boolean {
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    return phoneRegex.test(cleanPhone);
}

export {
    validateVideoItem,
    validateSchemaInput,
    validateContactPoint,
    validateBreadcrumbItem,
    validateEnhancedWebSite,
};

/**
 * Validates if a string is a valid country code or name.
 * Accepts ISO 2-letter codes, 3-letter codes, or full country names.
 *
 * @param country - Country code or name to validate
 * @returns True if country format is valid, false otherwise
 */
export function isValidCountryCode(country: string): boolean {
    if (country.length === 2) {
        return /^[A-Z]{2}$/i.test(country);
    } else if (country.length === 3) {
        return /^[A-Z]{3}$/i.test(country);
    } else {
        return country.length >= 2 && /^[A-Za-z\s\-.]+$/.test(country);
    }
}

/**
 * Validates contact point input for schema generation.
 *
 * @param input - Unknown input to validate as contact point data
 * @returns Result containing validated ContactPointInput or validation errors
 */
function validateContactPoint(input: unknown): Result<ContactPointInput, ValidationError[]> {
    const errors: ValidationError[] = [];

    if (!input || typeof input !== 'object') {
        return fail([{ field: 'contactType', message: 'ContactPoint must be an object' }]);
    }

    const contact = input as Record<string, unknown>;

    if (!contact.contactType || typeof contact.contactType !== 'string') {
        errors.push({
            field: 'contactType',
            message: 'Contact type is required and must be a string',
            value: contact.contactType,
        });
    }

    if (contact.email) {
        if (typeof contact.email !== 'string' || !isValidEmail(contact.email)) {
            errors.push({
                field: 'email',
                message: 'Email must be a valid email address',
                value: contact.email,
            });
        }
    }

    if (contact.telephone) {
        if (typeof contact.telephone !== 'string' || !isValidPhoneNumber(contact.telephone)) {
            errors.push({
                field: 'telephone',
                message: 'Telephone must be a valid phone number',
                value: contact.telephone,
            });
        }
    }

    if (contact.url) {
        if (typeof contact.url !== 'string' || !isValidUrl(contact.url)) {
            errors.push({
                field: 'url',
                message: 'URL must be a valid URL',
                value: contact.url,
            });
        }
    }

    if (contact.availableLanguages !== undefined) {
        if (!Array.isArray(contact.availableLanguages)) {
            errors.push({
                field: 'availableLanguages',
                message: 'Available languages must be an array',
                value: contact.availableLanguages,
            });
        } else {
            contact.availableLanguages.forEach((lang, index) => {
                if (typeof lang !== 'string') {
                    errors.push({
                        field: 'availableLanguages',
                        message: `Language at index ${index} must be a string`,
                        value: lang,
                    });
                }
            });
        }
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok(contact as unknown as ContactPointInput);
}

/**
 * Validates breadcrumb item input for schema generation.
 *
 * @param input - Unknown input to validate as breadcrumb data
 * @returns Result containing validated BreadcrumbItem or validation errors
 */
function validateBreadcrumbItem(input: unknown): Result<BreadcrumbItem, ValidationError[]> {
    const errors: ValidationError[] = [];

    if (!input || typeof input !== 'object') {
        return fail([{ field: 'name', message: 'BreadcrumbItem must be an object' }]);
    }

    const item = input as Record<string, unknown>;

    if (!item.name || typeof item.name !== 'string' || !item.name.trim()) {
        errors.push({
            field: 'name',
            message: 'Breadcrumb name is required and must be a non-empty string',
            value: item.name,
        });
    }

    if (!item.url || typeof item.url !== 'string') {
        errors.push({
            field: 'url',
            message: 'Breadcrumb URL is required and must be a string',
            value: item.url,
        });
    } else {
        const absoluteUrl = toAbsoluteUrl(item.url);
        if (!isValidUrl(absoluteUrl)) {
            errors.push({
                field: 'url',
                message: 'Breadcrumb URL must be a valid URL',
                value: item.url,
            });
        }
    }

    if (item.position !== undefined) {
        const position = Number(item.position);
        if (isNaN(position) || position < 1) {
            errors.push({
                field: 'position',
                message: 'Position must be a positive number',
                value: item.position,
            });
        }
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok({
        name: safeString(item.name).trim(),
        url: toAbsoluteUrl(safeString(item.url)),
        position: item.position ? Number(item.position) : 1,
    });
}

/**
 * Validates enhanced website input for schema generation.
 *
 * @param input - Unknown input to validate as website data
 * @returns Result containing validated EnhancedWebSiteInput or validation errors
 */
function validateEnhancedWebSite(input: unknown): Result<EnhancedWebSiteInput, ValidationError[]> {
    const errors: ValidationError[] = [];

    if (!input || typeof input !== 'object') {
        return fail([{ field: 'name', message: 'WebSite must be an object' }]);
    }

    const website = input as Record<string, unknown>;

    if (!website.name || typeof website.name !== 'string' || !website.name.trim()) {
        errors.push({
            field: 'name',
            message: 'Website name is required and must be a non-empty string',
            value: website.name,
        });
    }

    if (!website.url || typeof website.url !== 'string') {
        errors.push({
            field: 'url',
            message: 'Website URL is required and must be a string',
            value: website.url,
        });
    } else if (!isValidUrl(website.url)) {
        errors.push({
            field: 'url',
            message: 'Website URL must be a valid URL',
            value: website.url,
        });
    }

    if (website.search && typeof website.search === 'object') {
        const search = website.search as Record<string, unknown>;

        if (!search.searchUrl || typeof search.searchUrl !== 'string') {
            errors.push({
                field: 'searchUrl',
                message: 'Search URL is required when search is configured',
                value: search.searchUrl,
            });
        }

        if (!search.queryParam || typeof search.queryParam !== 'string') {
            errors.push({
                field: 'queryParam',
                message: 'Query parameter is required when search is configured',
                value: search.queryParam,
            });
        }
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok(website as unknown as EnhancedWebSiteInput);
}
